import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUserClock,
    faCog,
    faChartLine,
    faUsers,
    faHistory,
    faCheckCircle,
    faInfoCircle,
    faArrowRight,
    faArrowLeft,
    faRocket,
    faPaperPlane,
    faFilter,
    faBell
} from '@fortawesome/free-solid-svg-icons';
import DocumentationLayout from '../../../components/documentation/DocumentationLayout';

function MarketingWinback() {
    return (
        <DocumentationLayout>
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
                        <FontAwesomeIcon icon={faUserClock} className="text-purple-600 text-2xl" />
                    </div>
                    <h1 className="text-4xl font-bold text-gray-800 mb-4">
                        Recuperar Clientes Perdidos (WinBack)
                    </h1>
                    <p className="text-xl text-gray-600">
                        Aprende a utilizar el sistema automático de WinBack para recuperar clientes inactivos 
                        y reducir tu temporada baja mediante campañas inteligentes y personalizadas.
                    </p>
                </div>

                {/* Introducción */}
                <div className="bg-white rounded-lg shadow-md p-8 mb-8">
                    <h2 className="text-2xl font-bold text-gray-800 mb-4">
                        ¿Qué es WinBack?
                    </h2>
                    <p className="text-gray-600 mb-4">
                        WinBack es un sistema inteligente que te ayuda a recuperar clientes que han dejado de comprar 
                        en tu negocio. Utiliza análisis avanzado basado en segmentación RFM y frecuencia de compra 
                        para identificar cuándo un cliente está en riesgo y enviarle automáticamente una campaña 
                        personalizada para reactivarlo.
                    </p>
                    <div className="bg-purple-50 border-l-4 border-purple-500 p-4 mt-4">
                        <div className="flex items-start">
                            <FontAwesomeIcon icon={faInfoCircle} className="text-purple-600 mt-1 mr-3" />
                            <div>
                                <p className="text-purple-800 font-semibold mb-1">Sistema Inteligente</p>
                                <p className="text-purple-700 text-sm">
                                    WinBack calcula automáticamente el momento óptimo para contactar a cada cliente 
                                    basándose en su historial de compras y segmento RFM. No necesitas adivinar cuándo 
                                    un cliente está inactivo, el sistema lo hace por ti.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Checklist de Activación */}
                <section className="mb-12">
                    <div className="flex items-center mb-6">
                        <div className="bg-purple-100 p-3 rounded-lg mr-4">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-purple-600 text-xl" />
                        </div>
                        <h2 className="text-3xl font-bold text-gray-800">
                            Checklist de Activación
                        </h2>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <p className="text-gray-600 mb-6">
                            Al acceder al dashboard de WinBack, verás un checklist que te guía paso a paso para 
                            configurar correctamente el sistema. Sigue estos pasos en orden:
                        </p>
                        <div className="space-y-4">
                            <div className="border-l-4 border-blue-500 pl-4">
                                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                                    Paso 1: Configura tu WinBack
                                </h3>
                                <p className="text-gray-600 text-sm mb-2">
                                    Activa el sistema y ajusta los umbrales de inactividad según tus necesidades. 
                                    Puedes hacer clic en este paso para ir directamente a la sección de configuración.
                                </p>
                            </div>
                            <div className="border-l-4 border-green-500 pl-4">
                                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                                    Paso 2: Agrega tus primeros clientes
                                </h3>
                                <p className="text-gray-600 text-sm mb-2">
                                    Necesitas tener clientes registrados en tu marca para que WinBack pueda identificar 
                                    cuáles están inactivos. Si aún no tienes clientes, puedes agregarlos desde la 
                                    sección de Gestión de Clientes.
                                </p>
                            </div>
                            <div className="border-l-4 border-purple-500 pl-4">
                                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                                    Paso 3: Envía tu primer WinBack
                                </h3>
                                <p className="text-gray-600 text-sm mb-2">
                                    Prueba el sistema enviando una campaña manualmente a clientes de alta urgencia. 
                                    Esto te ayudará a entender cómo funciona y ver los resultados.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Configuración */}
                <section className="mb-12">
                    <div className="flex items-center mb-6">
                        <div className="bg-purple-100 p-3 rounded-lg mr-4">
                            <FontAwesomeIcon icon={faCog} className="text-purple-600 text-xl" />
                        </div>
                        <h2 className="text-3xl font-bold text-gray-800">
                            Configuración de WinBack
                        </h2>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Activar WinBack
                        </h3>
                        <p className="text-gray-600 mb-4">
                            Lo primero que debes hacer es activar WinBack para tu marca:
                        </p>
                        <ol className="space-y-3 text-gray-600">
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">1</span>
                                <span>Selecciona la marca para la que quieres configurar WinBack</span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">2</span>
                                <span>Ve a la sección de <strong>"Configuración"</strong> en el dashboard</span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">3</span>
                                <span>Activa el interruptor <strong>"WinBack Habilitado"</strong></span>
                            </li>
                        </ol>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Umbral de Inactividad Base
                        </h3>
                        <p className="text-gray-600 mb-4">
                            El umbral de inactividad base es el número de días que un cliente debe estar sin comprar 
                            para ser considerado inactivo. Por ejemplo, si estableces 30 días, cualquier cliente que 
                            no haya comprado en los últimos 30 días será identificado como inactivo.
                        </p>
                        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mt-4">
                            <p className="text-sm text-blue-800">
                                <strong>💡 Recomendación:</strong> Un umbral de 30 días es un buen punto de partida 
                                para la mayoría de negocios. Puedes ajustarlo según la frecuencia típica de compra 
                                de tus clientes.
                            </p>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Sistema Inteligente (Recomendado)
                        </h3>
                        <p className="text-gray-600 mb-4">
                            El sistema inteligente ajusta automáticamente el umbral de inactividad para cada cliente 
                            basándose en:
                        </p>
                        <ul className="space-y-2 text-gray-600 mb-4">
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Frecuencia histórica de compra:</strong> Si un cliente normalmente compra cada 10 días, 
                                el sistema lo considerará inactivo después de 10 días, no después del umbral base.</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Segmento RFM:</strong> Los clientes "Campeones" tienen umbrales más cortos que los 
                                "Perdidos", porque son más valiosos y es más importante recuperarlos rápido.</span>
                            </li>
                        </ul>
                        <div className="bg-green-50 border-l-4 border-green-500 p-4">
                            <p className="text-sm text-green-800">
                                <strong>✅ Activa siempre el sistema inteligente</strong> para obtener mejores resultados. 
                                El sistema calcula automáticamente el momento óptimo para contactar a cada cliente.
                            </p>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Multiplicadores por Segmento RFM
                        </h3>
                        <p className="text-gray-600 mb-4">
                            Los multiplicadores ajustan el umbral base según el segmento RFM del cliente. 
                            Valores más bajos significan umbrales más cortos (contactar antes):
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="border rounded-lg p-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Campeones (0.5)</h4>
                                <p className="text-sm text-gray-600">
                                    Clientes más valiosos. Se contactan cuando llevan la mitad del tiempo del umbral base.
                                </p>
                            </div>
                            <div className="border rounded-lg p-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Clientes Fieles (0.7)</h4>
                                <p className="text-sm text-gray-600">
                                    Clientes frecuentes. Se contactan cuando llevan el 70% del umbral base.
                                </p>
                            </div>
                            <div className="border rounded-lg p-4">
                                <h4 className="font-semibold text-gray-800 mb-2">En Riesgo (1.0)</h4>
                                <p className="text-sm text-gray-600">
                                    Clientes en riesgo. Se contactan cuando alcanzan el umbral base completo.
                                </p>
                            </div>
                            <div className="border rounded-lg p-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Perdidos (1.5)</h4>
                                <p className="text-sm text-gray-600">
                                    Clientes que llevan mucho tiempo sin comprar. Se contactan después de 1.5 veces el umbral base.
                                </p>
                            </div>
                        </div>
                        <div className="bg-yellow-50 border border-yellow-200 rounded p-4 mt-4">
                            <p className="text-xs text-yellow-800">
                                <strong>💡 Consejo:</strong> Puedes ajustar estos multiplicadores según tu estrategia. 
                                Valores más bajos para segmentos valiosos y más altos para segmentos menos prioritarios.
                            </p>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Envío Automático
                        </h3>
                        <p className="text-gray-600 mb-4">
                            Puedes activar el envío automático para que WinBack envíe campañas diariamente sin intervención:
                        </p>
                        <ul className="space-y-2 text-gray-600">
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Activado:</strong> El sistema ejecuta automáticamente todas las mañanas (9:00 AM) 
                                y envía WinBack a todos los clientes que cumplan los criterios de inactividad.</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-blue-500 mr-2 mt-1" />
                                <span><strong>Desactivado:</strong> Solo puedes enviar campañas manualmente desde la tabla de 
                                clientes inactivos.</span>
                            </li>
                        </ul>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Periodo de Cooldown
                        </h3>
                        <p className="text-gray-600 mb-4">
                            El periodo de cooldown es el tiempo mínimo que debe pasar entre dos campañas de WinBack 
                            para el mismo cliente. Esto evita saturar a tus clientes con demasiados mensajes.
                        </p>
                        <p className="text-gray-600 mb-4">
                            <strong>Ejemplo:</strong> Si estableces un cooldown de 30 días y envías un WinBack a un cliente 
                            hoy, no recibirá otro WinBack hasta dentro de 30 días, incluso si vuelve a estar inactivo antes.
                        </p>
                        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mt-4">
                            <p className="text-sm text-blue-800">
                                <strong>💡 Recomendación:</strong> Un periodo de cooldown de 30 días es adecuado para 
                                la mayoría de negocios. Ajusta según la frecuencia típica de compra de tus clientes.
                            </p>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Tipo de Incentivo y Máximo de Recompensa
                        </h3>
                        <p className="text-gray-600 mb-4">
                            Puedes configurar el tipo de incentivo que se incluirá en las campañas de WinBack:
                        </p>
                        <ul className="space-y-2 text-gray-600 mb-4">
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Light:</strong> Incentivos ligeros (descuentos pequeños, envío gratis)</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Medium:</strong> Incentivos moderados (descuentos medios)</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Strong:</strong> Incentivos fuertes (descuentos grandes, ofertas especiales)</span>
                            </li>
                        </ul>
                        <p className="text-gray-600 mb-4">
                            También puedes establecer el porcentaje máximo de recompensa (ej: 30% = máximo 30% de descuento).
                        </p>
                        <div className="bg-purple-50 border-l-4 border-purple-500 p-4">
                            <p className="text-sm text-purple-800">
                                <strong>💡 Consejo:</strong> Empieza con incentivos "Light" y aumenta gradualmente si 
                                no obtienes los resultados esperados. Los incentivos más fuertes pueden ser más efectivos 
                                pero también más costosos.
                            </p>
                        </div>
                    </div>
                </section>

                {/* KPIs y Estadísticas */}
                <section className="mb-12">
                    <div className="flex items-center mb-6">
                        <div className="bg-purple-100 p-3 rounded-lg mr-4">
                            <FontAwesomeIcon icon={faChartLine} className="text-purple-600 text-xl" />
                        </div>
                        <h2 className="text-3xl font-bold text-gray-800">
                            KPIs y Estadísticas
                        </h2>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Métricas Principales (KPIs)
                        </h3>
                        <p className="text-gray-600 mb-4">
                            En la parte superior del dashboard verás cuatro métricas clave:
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="border rounded-lg p-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Total Campañas</h4>
                                <p className="text-sm text-gray-600">
                                    Número total de campañas de WinBack enviadas (automáticas y manuales).
                                </p>
                            </div>
                            <div className="border rounded-lg p-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Enviados</h4>
                                <p className="text-sm text-gray-600">
                                    Número total de mensajes WinBack enviados a clientes.
                                </p>
                            </div>
                            <div className="border rounded-lg p-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Convertidos</h4>
                                <p className="text-sm text-gray-600">
                                    Número de clientes que realizaron una compra después de recibir un WinBack.
                                </p>
                            </div>
                            <div className="border rounded-lg p-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Tasa de Conversión</h4>
                                <p className="text-sm text-gray-600">
                                    Porcentaje de clientes que compraron después de recibir un WinBack (Convertidos / Enviados).
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Gráficos Estadísticos
                        </h3>
                        <p className="text-gray-600 mb-4">
                            La sección de estadísticas muestra gráficos visuales que te ayudan a entender el rendimiento 
                            de tus campañas WinBack:
                        </p>
                        <ul className="space-y-2 text-gray-600">
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Evolución temporal:</strong> Cómo han evolucionado las métricas a lo largo del tiempo</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Distribución por segmento:</strong> Qué segmentos RFM responden mejor a las campañas</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Tasa de conversión por urgencia:</strong> Qué nivel de urgencia genera más conversiones</span>
                            </li>
                        </ul>
                    </div>
                </section>

                {/* Clientes Inactivos */}
                <section className="mb-12">
                    <div className="flex items-center mb-6">
                        <div className="bg-purple-100 p-3 rounded-lg mr-4">
                            <FontAwesomeIcon icon={faUsers} className="text-purple-600 text-xl" />
                        </div>
                        <h2 className="text-3xl font-bold text-gray-800">
                            Clientes Inactivos
                        </h2>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Ver Clientes Inactivos
                        </h3>
                        <p className="text-gray-600 mb-4">
                            La tabla de clientes inactivos muestra todos los clientes que cumplen los criterios de inactividad 
                            según tu configuración. Para cada cliente verás:
                        </p>
                        <ul className="space-y-2 text-gray-600 mb-4">
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Información del cliente:</strong> Nombre, email, teléfono</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Días inactivo:</strong> Cuántos días lleva sin comprar</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Segmento RFM:</strong> A qué segmento pertenece el cliente</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Umbral recomendado:</strong> Días de inactividad recomendados por el sistema inteligente</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Urgencia:</strong> Nivel de urgencia (Alta 🔴, Media 🟡, Baja 🟢)</span>
                            </li>
                        </ul>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Filtrar Clientes Inactivos
                        </h3>
                        <p className="text-gray-600 mb-4">
                            Puedes filtrar la lista de clientes inactivos para encontrar específicamente lo que buscas:
                        </p>
                        <div className="space-y-4">
                            <div className="border-l-4 border-blue-500 pl-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Filtro por Urgencia</h4>
                                <p className="text-sm text-gray-600 mb-2">
                                    Filtra por nivel de urgencia:
                                </p>
                                <ul className="text-sm text-gray-600 space-y-1">
                                    <li>• <strong>Alta 🔴:</strong> Clientes que llevan mucho tiempo inactivos o son muy valiosos</li>
                                    <li>• <strong>Media 🟡:</strong> Clientes con urgencia moderada</li>
                                    <li>• <strong>Baja 🟢:</strong> Clientes con urgencia baja</li>
                                </ul>
                            </div>
                            <div className="border-l-4 border-purple-500 pl-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Filtro por Segmento RFM</h4>
                                <p className="text-sm text-gray-600 mb-2">
                                    Filtra por segmento RFM:
                                </p>
                                <ul className="text-sm text-gray-600 space-y-1">
                                    <li>• Campeones</li>
                                    <li>• Clientes Fieles</li>
                                    <li>• En Riesgo</li>
                                    <li>• No Pueden Perderse</li>
                                    <li>• A Punto de Dormir</li>
                                    <li>• Perdidos</li>
                                    <li>• Sin Compras</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Enviar WinBack Manualmente
                        </h3>
                        <p className="text-gray-600 mb-4">
                            Puedes enviar campañas WinBack manualmente a clientes específicos:
                        </p>
                        <ol className="space-y-3 text-gray-600">
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">1</span>
                                <span>Encuentra el cliente en la tabla de clientes inactivos</span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">2</span>
                                <span>Haz clic en el botón <strong>"Enviar WinBack"</strong> (icono de avión de papel)</span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">3</span>
                                <span>Se abrirá un modal con información del cliente y recomendaciones del sistema</span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">4</span>
                                <span>Revisa la información y haz clic en <strong>"Enviar WinBack"</strong> para confirmar</span>
                            </li>
                        </ol>
                        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mt-4">
                            <p className="text-sm text-blue-800">
                                <strong>💡 Consejo:</strong> Empieza enviando WinBack manualmente a clientes de alta urgencia 
                                para entender cómo funciona el sistema antes de activar el envío automático.
                            </p>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Niveles de Urgencia
                        </h3>
                        <p className="text-gray-600 mb-4">
                            El sistema calcula automáticamente el nivel de urgencia para cada cliente basándose en:
                        </p>
                        <ul className="space-y-2 text-gray-600">
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-red-500 mr-2 mt-1" />
                                <span><strong>Alta 🔴:</strong> Clientes muy valiosos (Campeones, Clientes Fieles) que llevan 
                                mucho tiempo inactivos, o clientes que superan significativamente su umbral recomendado.</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-yellow-500 mr-2 mt-1" />
                                <span><strong>Media 🟡:</strong> Clientes con urgencia moderada, generalmente aquellos que 
                                están cerca de su umbral recomendado.</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Baja 🟢:</strong> Clientes que acaban de alcanzar el umbral o tienen menor valor 
                                para el negocio.</span>
                            </li>
                        </ul>
                    </div>
                </section>

                {/* Historial de Campañas */}
                <section className="mb-12">
                    <div className="flex items-center mb-6">
                        <div className="bg-purple-100 p-3 rounded-lg mr-4">
                            <FontAwesomeIcon icon={faHistory} className="text-purple-600 text-xl" />
                        </div>
                        <h2 className="text-3xl font-bold text-gray-800">
                            Historial de Campañas
                        </h2>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Ver Historial de Campañas
                        </h3>
                        <p className="text-gray-600 mb-4">
                            El historial de campañas muestra todas las campañas WinBack que se han enviado, tanto automáticas 
                            como manuales. Para cada campaña verás:
                        </p>
                        <ul className="space-y-2 text-gray-600">
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Fecha y hora:</strong> Cuándo se envió la campaña</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Tipo:</strong> Automática o manual</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Número de clientes:</strong> Cuántos clientes recibieron la campaña</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Estado:</strong> Estado actual de la campaña (Enviada, En proceso, Completada)</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Resultados:</strong> Número de conversiones y tasa de conversión</span>
                            </li>
                        </ul>
                        <div className="bg-purple-50 border-l-4 border-purple-500 p-4 mt-4">
                            <p className="text-sm text-purple-800">
                                <strong>📊 Análisis:</strong> Revisa regularmente el historial para identificar qué tipos de 
                                campañas funcionan mejor y ajustar tu estrategia en consecuencia.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Consejos Finales */}
                <div className="bg-purple-50 rounded-lg p-8 mb-8">
                    <h3 className="text-lg font-semibold text-purple-800 mb-4">
                        💡 Consejos para un WinBack Exitoso
                    </h3>
                    <ul className="space-y-3 text-gray-700">
                        <li className="flex items-start">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-purple-600 mr-2 mt-1" />
                            <span><strong>Activa el sistema inteligente:</strong> Siempre usa el sistema inteligente para obtener 
                            umbrales personalizados y mejores resultados.</span>
                        </li>
                        <li className="flex items-start">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-purple-600 mr-2 mt-1" />
                            <span><strong>Empieza con envío manual:</strong> Prueba el sistema enviando manualmente antes de 
                            activar el envío automático para entender cómo funciona.</span>
                        </li>
                        <li className="flex items-start">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-purple-600 mr-2 mt-1" />
                            <span><strong>Prioriza clientes de alta urgencia:</strong> Enfócate primero en recuperar clientes 
                            valiosos que están en riesgo.</span>
                        </li>
                        <li className="flex items-start">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-purple-600 mr-2 mt-1" />
                            <span><strong>Revisa las métricas regularmente:</strong> Analiza qué segmentos y tipos de incentivos 
                            funcionan mejor para optimizar tus campañas.</span>
                        </li>
                        <li className="flex items-start">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-purple-600 mr-2 mt-1" />
                            <span><strong>Ajusta los multiplicadores:</strong> Si ves que ciertos segmentos no responden bien, 
                            ajusta los multiplicadores para contactarlos antes o después.</span>
                        </li>
                        <li className="flex items-start">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-purple-600 mr-2 mt-1" />
                            <span><strong>Respeta el periodo de cooldown:</strong> No satures a tus clientes con demasiados 
                            mensajes. El periodo de cooldown ayuda a mantener una comunicación saludable.</span>
                        </li>
                    </ul>
                </div>

                {/* Navegación */}
                <div className="flex justify-between items-center pt-6 border-t">
                    <Link
                        to="/documentation/marketing"
                        className="text-gray-600 hover:text-gray-800 font-medium inline-flex items-center"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                        Volver a Marketing
                    </Link>
                    <Link
                        to="/documentation/integraciones"
                        className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors inline-flex items-center"
                    >
                        Siguiente: Integraciones
                        <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                    </Link>
                </div>
            </div>
        </DocumentationLayout>
    );
}

export default MarketingWinback;

